import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import MonitorDoorCardV2 from './MonitorDoorCardV2';
import { getFloorMaxBadgeRowCount, getFloorMaxBelowBlockHeightPx, USE_DUMMY_MONITOR_FOOTER_AND_PROGRESS } from './monitorDoorCardModel';
import CompactRoomMonitorCircle from './CompactRoomMonitorCircle';
import CompactRoomMonitorCellWithDoorHover from './CompactRoomMonitorCellWithDoorHover';
import RoomStatusLegend from './RoomStatusLegend';
import nbrApi from '../../../services/nbrApi';
import { convertDataToFloorStructure } from '../../../services/common.utils';
import RoomStatusSnapshots from './RoomStatusSnapshots';
import FullPageLoader from '../../components/common/FullPageLoader';
import ReservationProfile from '../ReservationProfile/ReservationProfile';
import BulkRoomAssignmentModal from '../ReservationProfile/BulkRoomAssignmentModal';
import moment from 'moment';
import {
    BADGE_DISPLAY_CONFIRMATION,
    BADGE_DISPLAY_GUEST,
    readStoredBadgeDisplay,
    writeStoredBadgeDisplay,
    readStoredCompactRoomCells,
    writeStoredCompactRoomCells,
    readStoredShowUnassignedReservations,
    writeStoredShowUnassignedReservations,
    getMonitorBadgeLabel,
    getMonitorStandaloneBadgePresentation,
    getMonitorStandaloneBadgeShellStyle,
    getMonitorUnassignedBadgeOuterStyle,
    MONITOR_BADGE_CHECKIN_COLOR,
    MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX,
    MONITOR_UNASSIGNED_BADGE_ROOM_TYPE_COLOR
} from '../../../services/monitorBadgeDisplay';
import {
    getMonitorBuildingKey,
    getMonitorFloorRowKey,
    groupMonitorFloorsByBuilding,
    monitorPropertyHasBuildingData,
    readStoredBuildingGrouping,
    sortMonitorFloorsDesc,
    writeStoredBuildingGrouping
} from '../../../services/monitorBuildingGrouping';
import {
    reservationRowForDates,
    getReservationAssignedRoomNormalized,
    isReservationUnassignedForMonitor,
    reservationHasCheckinTodayForMonitor,
    reservationHasCheckoutTodayForMonitor,
    enrichMonitorRoomsWithAssignedReservations,
    getReservationArrivalMoment,
    getReservationDepartureMoment
} from '../../../services/monitorReservationUtils';
import { isRoomOccupied } from '../../../services/guestRoomIcons';

/** BFF may expose PMS property id on the root, nested payload, or on room/reservation rows */
function pickPmsPropertiesIdFromRoomStatusResponse(data) {
    if (!data || typeof data !== 'object') return null;
    const tryVal = (v) =>
        v != null && String(v).trim() !== '' && String(v).trim().toLowerCase() !== 'undefined'
            ? v
            : null;
    const root =
        tryVal(data.pms_properties_id) ??
        tryVal(data.pmsPropertiesId) ??
        tryVal(data.pms_property_id);
    if (root != null) return root;
    const inner = data.data;
    if (inner && typeof inner === 'object') {
        const fromInner =
            tryVal(inner.pms_properties_id) ??
            tryVal(inner.pmsPropertiesId) ??
            tryVal(inner.pms_property_id);
        if (fromInner != null) return fromInner;
    }
    const rooms = data.rooms_array;
    if (Array.isArray(rooms)) {
        for (const r of rooms) {
            const v =
                tryVal(r?.pms_properties_id) ??
                tryVal(r?.pmsPropertiesId) ??
                tryVal(r?.properties_pms_id);
            if (v != null) return v;
        }
    }
    const resvs = data.reservations_array;
    if (Array.isArray(resvs)) {
        for (const r of resvs) {
            const v = tryVal(r?.pms_properties_id) ?? tryVal(r?.pmsPropertiesId);
            if (v != null) return v;
        }
    }
    return null;
}

// Sample cart_resource_initials for demo (staff initials shown on monitor cards)
const SAMPLE_CART_INITIALS = ['JD', 'MK', 'AS', 'RJ', 'TB'];

function applySampleCartInitials(floors) {
    if (!floors?.length) return floors;
    let sampleIndex = 0;
    return floors.map((floor) => ({
        ...floor,
        // rooms: floor.rooms.map((room) => {
        //     if (room.cart_resource_initials) return room;
        //     const initials = SAMPLE_CART_INITIALS[sampleIndex % SAMPLE_CART_INITIALS.length];
        //     sampleIndex += 1;
        //     return { ...room, cart_resource_initials: initials };
        // })
    }));
}

// Dummy data to test progress bar with checkin/checkout badges (first room on first 2 floors)
function applySampleProgressAndBadges(floors) {
    if (!floors?.length) return floors;
    const now = new Date();
    const startedMinutesAgo = 15;
    const timeStart = new Date(now.getTime() - startedMinutesAgo * 60 * 1000).toISOString();
    const functionSecondsAllocated = 3600;

    return floors.map((floor, floorIndex) => ({
        ...floor,
        // rooms: floor.rooms.map((room, roomIndex) => {
        //     const isTestRoom = floorIndex < 2 && roomIndex === 0;
        //     if (!isTestRoom) return room;
        //     return {
        //         ...room,
        //         time_start: room.time_start || timeStart,
        //         function_seconds_allocated: room.function_seconds_allocated ?? functionSecondsAllocated,
        //         confirmation_numbers: room.confirmation_numbers ?? [
        //             { confirmation_number: '102010', checkout_today: true, checkin_today: false },
        //             { confirmation_number: '102023', checkout_today: false, checkin_today: true },
        //         ],
        //     };
        // })
    }));
}

/** Fixed select width from longest option label (ch), with room for caret/padding. */
function getMonitorFilterSelectWidthCh(labels, { minCh = 16, maxCh = 40, padCh = 4 } = {}) {
    let longest = 0;
    for (const label of labels) {
        const len = String(label ?? '').length;
        if (len > longest) longest = len;
    }
    return `${Math.min(maxCh, Math.max(minCh, longest + padCh))}ch`;
}

function formatServiceStatusDisplay(value) {
    if (!value) return '';
    return value
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

// Sample data so "Turning Over Today" has rooms to show (both checkout + checkin today)
function applySampleTurnoverRooms(floors) {
    if (!floors?.length) return floors;
    const turnoverBadges = [
        { confirmation_number: '102010', checkout_today: true, checkin_today: false },
        { confirmation_number: '102023', checkout_today: false, checkin_today: true },
    ];
    return floors.map((floor, floorIndex) => ({
        ...floor,
        // rooms: floor.rooms.map((room, roomIndex) => {
        //     // Mark first room on first 3 floors as turnover for testing
        //     const isTurnoverSample = floorIndex < 3 && roomIndex === 0;
        //     if (!isTurnoverSample) return room;
        //     return {
        //         ...room,
        //         checkin_today: true,
        //         checkout_today: true,
        //         confirmation_numbers: room.confirmation_numbers?.length
        //             ? room.confirmation_numbers
        //             : turnoverBadges,
        //     };
        // })
    }));
}

/**
 * Local QA: set to `true` to merge dummy check-in / checking-in rows after `/getroomstatuses`.
 * Set back to `false` before shipping; does not hit the network.
 */
let USE_DUMMY_MONITOR_CHECKIN_DATA = false;

/**
 * Local QA: set to `true` to merge inspector icon + initials on room `DUMMY_MONITOR_INSPECTOR_ROOM_NUMBER`.
 * Controlled by `USE_DUMMY_MONITOR_FOOTER_AND_PROGRESS` in monitorDoorCardModel.js.
 */
const DUMMY_MONITOR_INSPECTOR_ROOM_NUMBER = 720;

/**
 * Local QA — turnover stacked badges (one card, check-in on top + check-out below):
 * Set `USE_DUMMY_MONITOR_TURNOVER_STACKED_BADGES = true`, reload Monitor.
 * Patches room `DUMMY_MONITOR_TURNOVER_STACKED_BADGES_ROOM_NUMBER` if present; otherwise appends a sample door on floor 5.
 * Set back to `false` before shipping.
 */
let USE_DUMMY_MONITOR_TURNOVER_STACKED_BADGES = false;

const DUMMY_MONITOR_TURNOVER_STACKED_BADGES_ROOM_NUMBER = 721;

const DUMMY_MONITOR_TURNOVER_STACKED_BADGES_ROWS = [
    {
        confirmation_number: '103255',
        checkout_today: false,
        checkin_today: true,
        guest_first: 'Rob',
        guest_last: 'Guedry'
    },
    {
        confirmation_number: '103254',
        checkout_today: true,
        checkin_today: false,
        guest_first: 'Coco',
        guest_last: 'Lewis'
    }
];

function buildDummyMonitorTurnoverStackedBadgesRoom(room) {
    return {
        ...room,
        dummy_show_two_confirmation_badges: true,
        checkin_today: true,
        checkout_today: true,
        confirmation_numbers: DUMMY_MONITOR_TURNOVER_STACKED_BADGES_ROWS,
        confirmation_number: DUMMY_MONITOR_TURNOVER_STACKED_BADGES_ROWS[0].confirmation_number
    };
}

function applyDummyMonitorTwoBadgesRoom(floors) {
    if (!USE_DUMMY_MONITOR_TURNOVER_STACKED_BADGES || !floors?.length) return floors;

    let patchedExisting = false;
    const patchedFloors = floors.map((floor) => ({
        ...floor,
        rooms: floor.rooms.map((room) => {
            const n = Number(room.roomNumber ?? room.room_number);
            if (!Number.isFinite(n) || n !== DUMMY_MONITOR_TURNOVER_STACKED_BADGES_ROOM_NUMBER) {
                return room;
            }
            patchedExisting = true;
            return buildDummyMonitorTurnoverStackedBadgesRoom(room);
        })
    }));

    if (patchedExisting) {
        return patchedFloors;
    }

    const targetFloor = 5;
    const floorIndex = patchedFloors.findIndex((f) => Number(f.floor) === targetFloor);
    const insertAt = floorIndex >= 0 ? floorIndex : 0;
    const hostFloor = patchedFloors[insertAt];
    const template = hostFloor?.rooms?.[0] ?? {};
    const syntheticRoom = buildDummyMonitorTurnoverStackedBadgesRoom({
        ...template,
        roomNumber: DUMMY_MONITOR_TURNOVER_STACKED_BADGES_ROOM_NUMBER,
        room_number: String(DUMMY_MONITOR_TURNOVER_STACKED_BADGES_ROOM_NUMBER),
        floor: Number(hostFloor.floor),
        status: template.status || 'Needs Inspection',
        statusColor: template.statusColor || 'clean',
        status_display_text: template.status_display_text || 'Needs Inspection',
        status_display_backcolor: template.status_display_backcolor || '#cba4de',
        status_display_forecolor: template.status_display_forecolor || '#585858',
        status_display_inner_border_color: template.status_display_inner_border_color || '#ffffff',
        status_display_outer_border_color: template.status_display_outer_border_color || '#cba4de',
        guest_presence: 'vacant',
        occupied: 0,
        clean_state: 'clean',
        inspection_state: 'pending'
    });

    return patchedFloors.map((floor, index) =>
        index === insertAt ? { ...floor, rooms: [...floor.rooms, syntheticRoom] } : floor
    );
}

/**
 * Local QA: when `true`, room `DUMMY_MONITOR_ONE_BADGE_IN_TIME_ROOM_NUMBER` gets a single check-in-today
 * confirmation row (one green “in time” badge). Flag `dummy_show_one_confirmation_badge_in_time` is set for clarity.
 */
let USE_DUMMY_MONITOR_ONE_BADGE_IN_TIME = false;

const DUMMY_MONITOR_ONE_BADGE_IN_TIME_ROOM_NUMBER = 508;

const DUMMY_MONITOR_ONE_BADGE_IN_TIME_ROW = {
    confirmation_number: '102900',
    checkout_today: false,
    checkin_today: true,
    guest_first: 'Jordan',
    guest_last: 'QA',
};

function applyDummyMonitorOneBadgeInTimeRoom(floors) {
    if (!USE_DUMMY_MONITOR_ONE_BADGE_IN_TIME || !floors?.length) return floors;
    return floors.map((floor) => ({
        ...floor,
        rooms: floor.rooms.map((room) => {
            const n = Number(room.roomNumber);
            if (!Number.isFinite(n) || n !== DUMMY_MONITOR_ONE_BADGE_IN_TIME_ROOM_NUMBER) {
                return room;
            }
            return {
                ...room,
                dummy_show_one_confirmation_badge_in_time: true,
                confirmation_number: DUMMY_MONITOR_ONE_BADGE_IN_TIME_ROW.confirmation_number,
                checkin_today: true,
                checkout_today: false,
                confirmation_numbers: [DUMMY_MONITOR_ONE_BADGE_IN_TIME_ROW],
            };
        }),
    }));
}

function applyDummyMonitorInspectorRoom(floors) {
    if (!USE_DUMMY_MONITOR_FOOTER_AND_PROGRESS || !floors?.length) return floors;
    return floors.map((floor) => ({
        ...floor,
        rooms: floor.rooms.map((room) => {
            const n = Number(room.roomNumber);
            if (!Number.isFinite(n) || n !== DUMMY_MONITOR_INSPECTOR_ROOM_NUMBER) {
                return room;
            }
            return {
                ...room,
                show_monitor_inspector_footer: true,
                cart_resource_initials: 'DD',
                show_icon: 1,
            };
        }),
    }));
}

/**
 * Local QA — task progress bar + cart icon on sample room(s).
 * Controlled by `USE_DUMMY_MONITOR_FOOTER_AND_PROGRESS` in monitorDoorCardModel.js.
 */
const DUMMY_MONITOR_PROGRESS_ROOM_NUMBER = 714;

const DUMMY_MONITOR_PROGRESS_STARTED_MINUTES_AGO = 15;
const DUMMY_MONITOR_PROGRESS_SECONDS_ALLOCATED = 3600;

function buildDummyMonitorProgressRoom(room) {
    const now = new Date();
    const timeStart = new Date(
        now.getTime() - DUMMY_MONITOR_PROGRESS_STARTED_MINUTES_AGO * 60 * 1000
    ).toISOString();

    return {
        ...room,
        dummy_show_progress_bar: true,
        time_start: room.time_start || timeStart,
        function_seconds_allocated:
            room.function_seconds_allocated ?? DUMMY_MONITOR_PROGRESS_SECONDS_ALLOCATED
    };
}

function applyDummyMonitorProgressBar(floors) {
    if (!USE_DUMMY_MONITOR_FOOTER_AND_PROGRESS || !floors?.length) return floors;

    let patchedExisting = false;
    const patchedFloors = floors.map((floor) => ({
        ...floor,
        rooms: floor.rooms.map((room) => {
            const n = Number(room.roomNumber ?? room.room_number);
            if (!Number.isFinite(n) || n !== DUMMY_MONITOR_PROGRESS_ROOM_NUMBER) {
                return room;
            }
            patchedExisting = true;
            return buildDummyMonitorProgressRoom(room);
        })
    }));

    if (patchedExisting) {
        return patchedFloors;
    }

    return patchedFloors.map((floor, floorIndex) =>
        floorIndex === 0
            ? {
                ...floor,
                rooms: floor.rooms.map((room, roomIndex) =>
                    roomIndex === 0 ? buildDummyMonitorProgressRoom(room) : room
                )
            }
            : floor
    );
}

/**
 * Local QA — housekeeper cart icon + staff initials under door.
 * Controlled by `USE_DUMMY_MONITOR_FOOTER_AND_PROGRESS` in monitorDoorCardModel.js.
 */
const DUMMY_MONITOR_CART_ROOM_NUMBER = 714;

const DUMMY_MONITOR_CART_INITIALS = 'KL';

function buildDummyMonitorCartRoom(room) {
    return {
        ...room,
        dummy_show_cart_icon: true,
        cart_resource_initials: room.cart_resource_initials || DUMMY_MONITOR_CART_INITIALS,
        show_icon: 1,
        show_monitor_inspector_footer: false,
        role_name: room.role_name === 'room inspector' ? undefined : room.role_name
    };
}

function applyDummyMonitorCartIcon(floors) {
    if (!USE_DUMMY_MONITOR_FOOTER_AND_PROGRESS || !floors?.length) return floors;

    let patchedExisting = false;
    const patchedFloors = floors.map((floor) => ({
        ...floor,
        rooms: floor.rooms.map((room) => {
            const n = Number(room.roomNumber ?? room.room_number);
            if (!Number.isFinite(n) || n !== DUMMY_MONITOR_CART_ROOM_NUMBER) {
                return room;
            }
            patchedExisting = true;
            return buildDummyMonitorCartRoom(room);
        })
    }));

    if (patchedExisting) {
        return patchedFloors;
    }

    return patchedFloors.map((floor, floorIndex) =>
        floorIndex === 0
            ? {
                ...floor,
                rooms: floor.rooms.map((room, roomIndex) =>
                    roomIndex === 0 ? buildDummyMonitorCartRoom(room) : room
                )
            }
            : floor
    );
}

/** First room on lowest floor: assigned guest, check-in today, not occupied (Arrivals → Checking In grid). */
function applyDummyMonitorCheckinRooms(floors) {
    if (!USE_DUMMY_MONITOR_CHECKIN_DATA || !floors?.length) return floors;

    const firstFloorIndex = 0;
    const dummyConfAssigned = 'DUMMY-CHKIN-ROOM';

    return floors.map((floor, fi) => {
        if (fi !== firstFloorIndex) return floor;
        return {
            ...floor,
            rooms: floor.rooms.map((room, ri) => {
                if (ri !== 0) return room;
                return {
                    ...room,
                    occupied: 0,
                    checkin_today: true,
                    checkout_today: false,
                    confirmation_number: dummyConfAssigned,
                    reservations_id: room.reservations_id ?? 999001,
                    guest_first: room.guest_first || 'Dummy',
                    guest_last: room.guest_last || 'CheckIn',
                    confirmation_numbers: [
                        {
                            confirmation_number: dummyConfAssigned,
                            checkin_today: true,
                            checkout_today: false,
                            guest_first: 'Dummy',
                            guest_last: 'CheckIn',
                        },
                    ],
                };
            }),
        };
    });
}

/** Extra `reservations_array` row: unassigned, check-in today (Arrivals → Checking In unassigned floor). */
function applyDummyMonitorCheckinReservations(existing, dataRoot, propertiesId) {
    if (!USE_DUMMY_MONITOR_CHECKIN_DATA) return existing;
    const base = Array.isArray(existing) ? existing : [];
    const pmsId = pickPmsPropertiesIdFromRoomStatusResponse(dataRoot || {}) ?? base[0]?.pms_properties_id ?? '';
    const today = moment().format('YYYY-MM-DD');
    return [
        ...base,
        {
            properties_id: propertiesId ?? base[0]?.properties_id ?? 0,
            pms_properties_id: pmsId,
            confirmation_number: 'DUMMY-UNASGN-CHKIN',
            reservations_id: 999002,
            guest_first: 'Dummy',
            guest_last: 'UnassignedArrival',
            room_type_name: base[0]?.room_type_name || 'Standard King',
            room_type_code: base[0]?.room_type_code || 'STKG',
            checkin_today: true,
            checkout_today: false,
            arrival_date: today,
        },
    ];
}

function normalizeReservationSearchValue(value) {
    return value == null ? '' : String(value).trim().toLowerCase();
}

function reservationRowMatchesSearch(row, normalizedSearch) {
    if (!row || !normalizedSearch) return false;

    const normalizedRow = reservationRowForDates(row);
    const nestedReservation = row?.reservation && typeof row.reservation === 'object'
        ? row.reservation
        : null;

    const searchFields = [
        normalizedRow?.confirmation_number,
        normalizedRow?.confirmationNumber,
        normalizedRow?.reservation_confirmation_number,
        normalizedRow?.guest_last,
        normalizedRow?.guestLast,
        normalizedRow?.last_name,
        normalizedRow?.guest_surname,
        normalizedRow?.guest_name,
        normalizedRow?.guest_full_name,
        nestedReservation?.confirmation_number,
        nestedReservation?.guest_last,
        nestedReservation?.last_name,
        nestedReservation?.guest_name,
        nestedReservation?.guest_full_name
    ];

    return searchFields.some((field) =>
        normalizeReservationSearchValue(field).includes(normalizedSearch)
    );
}

function roomMatchesReservationSearch(room, reservationsArray, searchTerm) {
    const normalizedSearch = normalizeReservationSearchValue(searchTerm);
    if (!normalizedSearch) return true;

    const roomReservationRows = [
        room,
        ...(Array.isArray(room?.confirmation_numbers) ? room.confirmation_numbers : [])
    ];

    if (roomReservationRows.some((row) => reservationRowMatchesSearch(row, normalizedSearch))) {
        return true;
    }

    const roomNumber = room?.roomNumber ?? room?.room_number;
    if (roomNumber == null || String(roomNumber).trim() === '' || !Array.isArray(reservationsArray)) {
        return false;
    }

    return reservationsArray.some((reservation) => {
        const assignedRoom = getReservationAssignedRoomNormalized(reservation);
        return assignedRoom &&
            String(assignedRoom) === String(roomNumber) &&
            reservationRowMatchesSearch(reservation, normalizedSearch);
    });
}

const ARRIVALS_DEPARTURES_FILTER_KEYS = [
    'show_all',
    'checkin',
    'checked_in',
    'stayover',
    'checkout',
    'turnover'
];

const ARRIVALS_DEPARTURES_FILTER_LABELS = {
    show_all: {
        label: 'Show All',
        icon: 'bi-grid-3x3-gap',
        color: 'secondary',
        description: 'All checking in, checked in, stayovers, checking out, and turning over today'
    },
    checkin: {
        label: 'Checking In',
        icon: 'bi-box-arrow-in-down',
        color: 'success',
        description: 'Check-in today, not yet occupied'
    },
    checked_in: {
        label: 'Checked In',
        icon: 'bi-person-check',
        color: 'primary',
        description: 'Rooms checked in today and currently occupied'
    },
    stayover: {
        label: 'Stayovers',
        icon: 'bi-house-door',
        color: 'info',
        description: 'Rooms with no check-in or check-out today'
    },
    checkout: {
        label: 'Checking Out',
        icon: 'bi-box-arrow-up',
        color: 'danger',
        description: 'Rooms with departures today'
    },
    turnover: {
        label: 'Turning Over Today',
        icon: 'bi-arrow-left-right',
        color: 'warning',
        description:
            'Same-day changeovers; skips a single room row with both today flags unless two reservation segments show checkout and check-in today'
    }
};

function MonitorIndex() {
    const selectedLoginData = JSON.parse(localStorage.getItem('selectedLoginData')) || {}
    const userDetailsAll = JSON.parse(localStorage?.getItem('userDetailsAll')) || {}

    const [badgeDisplayMode, setBadgeDisplayMode] = useState(() =>
        readStoredBadgeDisplay(selectedLoginData)
    )

    useEffect(() => {
        writeStoredBadgeDisplay(selectedLoginData, badgeDisplayMode)
    }, [badgeDisplayMode, selectedLoginData?.properties_id, selectedLoginData?.email])

    const [useCompactRoomCells, setUseCompactRoomCells] = useState(() =>
        readStoredCompactRoomCells(selectedLoginData)
    );

    useEffect(() => {
        writeStoredCompactRoomCells(selectedLoginData, useCompactRoomCells);
    }, [useCompactRoomCells, selectedLoginData?.properties_id, selectedLoginData?.email]);

    const [showBuildingGrouping, setShowBuildingGrouping] = useState(() =>
        readStoredBuildingGrouping(selectedLoginData)
    );

    useEffect(() => {
        writeStoredBuildingGrouping(selectedLoginData, showBuildingGrouping);
    }, [showBuildingGrouping, selectedLoginData?.properties_id, selectedLoginData?.email]);

    const [showUnassignedReservations, setShowUnassignedReservations] = useState(() =>
        readStoredShowUnassignedReservations(selectedLoginData)
    );

    useEffect(() => {
        writeStoredShowUnassignedReservations(selectedLoginData, showUnassignedReservations);
    }, [showUnassignedReservations, selectedLoginData?.properties_id, selectedLoginData?.email]);

    const [monitorShowDate, setMonitorShowDate] = useState(() => moment().format('YYYY-MM-DD'))
    const monitorShowDateRef = useRef(monitorShowDate)
    monitorShowDateRef.current = monitorShowDate

    const [rooms, setRooms] = useState([])
    const hasBuildingData = useMemo(
        () => monitorPropertyHasBuildingData(rooms),
        [rooms]
    );
    const effectiveShowBuildingGrouping = showBuildingGrouping && hasBuildingData;
    const [reservations, setReservations] = useState([])
    const [roomAssignments, setRoomAssignments] = useState([]) // Store room_assignment_array for room recommendations
    const [loading, setLoading] = useState(false)
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('all')
    const [selectedServiceStatusFilter, setSelectedServiceStatusFilter] = useState('all')
    const [selectedRoomTypeFilter, setSelectedRoomTypeFilter] = useState('all')
    const [reservationSearchTerm, setReservationSearchTerm] = useState('')
    const [viewMode, setViewMode] = useState('floor') // 'floor', 'status', 'active', or 'arrivals_departures'
    const [arrivalsDeparturesFilter, setArrivalsDeparturesFilter] = useState('show_all') // 'show_all' | 'checkin' | 'checked_in' | 'stayover' | 'checkout' | 'turnover'
    const [showReservationModal, setShowReservationModal] = useState(false)
    const [selectedConfirmationNumber, setSelectedConfirmationNumber] = useState(null)
    /** 'assign' | 'show' | null — from /getroomstatuses-derived UI; null falls back to profile probe */
    const [selectedAssignmentContext, setSelectedAssignmentContext] = useState(null)
    const [showBulkRoomAssignmentModal, setShowBulkRoomAssignmentModal] = useState(false)
    /** Top-level fields from last successful /getroomstatuses (e.g. pms_properties_id for bulk assignment) */
    const [roomStatusesMeta, setRoomStatusesMeta] = useState(null)
    /** Collapse Show / layout / view tabs + floor status filters to maximize the room grid */
    const [monitorToolbarExpanded, setMonitorToolbarExpanded] = useState(true)
    /** Fill viewport via flex chain (#main-wrapper → scroll); avoids brittle px height math. */
    useLayoutEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const wrapper = document.getElementById('main-wrapper');
        const contentBody = wrapper?.querySelector('.content-body');
        const container = contentBody?.querySelector('.container-fluid');

        wrapper?.classList.add('monitor-layout-fill');
        contentBody?.classList.add('monitor-layout-fill__body');
        container?.classList.add('monitor-layout-fill__container');

        if (contentBody) {
            contentBody.style.minHeight = '0';
        }

        return () => {
            wrapper?.classList.remove('monitor-layout-fill');
            contentBody?.classList.remove('monitor-layout-fill__body');
            container?.classList.remove('monitor-layout-fill__container');
            if (contentBody) {
                contentBody.style.minHeight = '';
            }
        };
    }, []);

    const floorLabelStyle = useMemo(
        () =>
            useCompactRoomCells
                ? { width: 52, minWidth: 52, fontSize: 38, fontWeight: 500, lineHeight: 1 }
                : { width: 130, minWidth: 130, fontSize: 90, fontWeight: 500 },
        [useCompactRoomCells]
    );

    const monitorFloorRoomsRowClass = useCompactRoomCells
        ? ' align-items-end monitor-compact-floor-rooms'
        : ' align-items-start monitor-door-card-floor-rooms';

    /** Same bar as other room filters: only need a selected property (PMS id is resolved from API or inside the modal) */
    const canBulkAutoAssign = useMemo(() => {
        const pid = selectedLoginData?.properties_id;
        return pid != null && String(pid).trim() !== '';
    }, [selectedLoginData?.properties_id]);

    // Detect RoomOps role
    const roleName = userDetailsAll?.session_info?.role_name || '';
    const isRoomopsRole = roleName && (
        roleName.toLowerCase().includes('roomops') ||
        roleName.toLowerCase() === 'roomops monitor' ||
        roleName.toLowerCase() === 'roomops admin' ||
        roleName.toLowerCase() === 'roomops manager'
    );

    // Get unique status display texts from all rooms
    const uniqueStatusOptions = useMemo(() => {
        const allStatuses = [];
        rooms.forEach(floor => {
            floor.rooms.forEach(room => {
                if (room.status_display_text && !allStatuses.includes(room.status_display_text)) {
                    allStatuses.push(room.status_display_text);
                }
            });
        });
        return allStatuses.sort();
    }, [rooms]);

    // Get unique service status values from all rooms
    const uniqueServiceStatusOptions = useMemo(() => {
        const allServiceStatuses = [];
        rooms.forEach(floor => {
            floor.rooms.forEach(room => {
                if (room.service_status && !allServiceStatuses.includes(room.service_status)) {
                    allServiceStatuses.push(room.service_status);
                }
            });
        });
        return allServiceStatuses.sort();
    }, [rooms]);

    // Get unique room types from all rooms (code + name from live data)
    const uniqueRoomTypeOptions = useMemo(() => {
        const byKey = new Map();
        rooms.forEach((floor) => {
            floor.rooms.forEach((room) => {
                const roomTypeCode =
                    room?.room_type_code != null && String(room.room_type_code).trim() !== ''
                        ? String(room.room_type_code).trim()
                        : '';
                const roomTypeName =
                    room?.room_type_name != null && String(room.room_type_name).trim() !== ''
                        ? String(room.room_type_name).trim()
                        : '';
                if (!roomTypeCode && !roomTypeName) return;
                const key = `${roomTypeName} (${roomTypeCode})`;
                if (byKey.has(key)) return;
                byKey.set(key, {
                    key,
                    roomTypeCode,
                    roomTypeName,
                    label: roomTypeCode && roomTypeName
                        ? `${roomTypeCode} — ${roomTypeName}`
                        : roomTypeCode || roomTypeName
                });
            });
        });
        return Array.from(byKey.values()).sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        );
    }, [rooms]);

    const statusFilterSelectWidth = useMemo(
        () => getMonitorFilterSelectWidthCh(['🔄 All Statuses', ...uniqueStatusOptions]),
        [uniqueStatusOptions]
    );

    const serviceStatusFilterSelectWidth = useMemo(
        () =>
            getMonitorFilterSelectWidthCh([
                '🔄 All Service Statuses',
                ...uniqueServiceStatusOptions.map((s) => formatServiceStatusDisplay(s))
            ]),
        [uniqueServiceStatusOptions]
    );

    const roomTypeFilterSelectWidth = useMemo(
        () =>
            getMonitorFilterSelectWidthCh([
                '🔄 All Room Types',
                ...uniqueRoomTypeOptions.map((opt) => opt.label)
            ], { maxCh: 48 }),
        [uniqueRoomTypeOptions]
    );

    // After refresh, a selected status may no longer exist in room data. A controlled
    // <select> with a value missing from <option> list won't fire onChange for "All Statuses".
    useEffect(() => {
        if (selectedStatusFilter !== 'all' && !uniqueStatusOptions.includes(selectedStatusFilter)) {
            setSelectedStatusFilter('all');
        }
    }, [uniqueStatusOptions, selectedStatusFilter]);

    useEffect(() => {
        if (selectedServiceStatusFilter !== 'all' && !uniqueServiceStatusOptions.includes(selectedServiceStatusFilter)) {
            setSelectedServiceStatusFilter('all');
        }
    }, [uniqueServiceStatusOptions, selectedServiceStatusFilter]);

    useEffect(() => {
        if (
            selectedRoomTypeFilter !== 'all' &&
            !uniqueRoomTypeOptions.some((opt) => opt.key === selectedRoomTypeFilter)
        ) {
            setSelectedRoomTypeFilter('all');
        }
    }, [uniqueRoomTypeOptions, selectedRoomTypeFilter]);

    const getRoomTypeFilterKey = (room) => {
        const roomTypeCode =
            room?.room_type_code != null && String(room.room_type_code).trim() !== ''
                ? String(room.room_type_code).trim()
                : '';
        const roomTypeName =
            room?.room_type_name != null && String(room.room_type_name).trim() !== ''
                ? String(room.room_type_name).trim()
                : '';
        if (!roomTypeCode && !roomTypeName) return '';
        return `${roomTypeName} (${roomTypeCode})`;
    };

    // Filter rooms based on selected status
    // const getFilteredRooms = () => {
    //     if (selectedStatusFilter === 'all') {
    //         return rooms;
    //     }

    //     return rooms.map(floor => ({
    //         ...floor,
    //         rooms: floor.rooms.filter(room => room.status_display_text === selectedStatusFilter)
    //     })).filter(floor => floor.rooms.length > 0);
    // };
    const roomsForReservationFilter = useMemo(() => {
        if (!reservationSearchTerm.trim()) return rooms;
        return rooms
            .map((floor) => ({
                ...floor,
                rooms: floor.rooms.filter((room) =>
                    roomMatchesReservationSearch(room, reservations, reservationSearchTerm)
                ),
            }))
            .filter((floor) => floor.rooms.length > 0);
    }, [rooms, reservations, reservationSearchTerm]);

    const reservationsForReservationFilter = useMemo(() => {
        if (!reservationSearchTerm.trim()) return reservations;
        const normalizedSearch = normalizeReservationSearchValue(reservationSearchTerm);
        return reservations.filter((reservation) =>
            reservationRowMatchesSearch(reservation, normalizedSearch)
        );
    }, [reservations, reservationSearchTerm]);

    const getFilteredRooms = useMemo(() => {
        let filtered = roomsForReservationFilter;

        // Filter by status_display_text
        if (selectedStatusFilter !== 'all') {
            filtered = filtered.map(floor => ({
                ...floor,
                rooms: floor.rooms.filter(room => room.status_display_text === selectedStatusFilter)
            })).filter(floor => floor.rooms.length > 0);
        }

        // Filter by service_status
        if (selectedServiceStatusFilter !== 'all') {
            filtered = filtered.map(floor => ({
                ...floor,
                rooms: floor.rooms.filter(room => room.service_status === selectedServiceStatusFilter)
            })).filter(floor => floor.rooms.length > 0);
        }

        // Filter by room type (name + code from room data)
        if (selectedRoomTypeFilter !== 'all') {
            filtered = filtered.map((floor) => ({
                ...floor,
                rooms: floor.rooms.filter((room) => getRoomTypeFilterKey(room) === selectedRoomTypeFilter)
            })).filter((floor) => floor.rooms.length > 0);
        }

        return filtered;
    }, [roomsForReservationFilter, selectedStatusFilter, selectedServiceStatusFilter, selectedRoomTypeFilter]);

    const monitorToolbarCollapsedHint = useMemo(() => {
        const modeLabels = {
            floor: 'Floor view',
            status: 'Status view',
            active: 'Active rooms',
            arrivals_departures: 'Arrivals & departures'
        };
        const parts = [modeLabels[viewMode] || viewMode];
        if (monitorShowDate) {
            parts.push(moment(monitorShowDate, 'YYYY-MM-DD').format('MMM D, YYYY'));
        }
        const n = getFilteredRooms.reduce((t, f) => t + f.rooms.length, 0);
        parts.push(`${n} rooms`);
        if (selectedStatusFilter !== 'all') {
            parts.push(selectedStatusFilter);
        }
        if (selectedServiceStatusFilter !== 'all') {
            parts.push(formatServiceStatusDisplay(selectedServiceStatusFilter));
        }
        if (selectedRoomTypeFilter !== 'all') {
            const roomTypeOpt = uniqueRoomTypeOptions.find((opt) => opt.key === selectedRoomTypeFilter);
            parts.push(roomTypeOpt?.label || selectedRoomTypeFilter);
        }
        if (reservationSearchTerm.trim()) {
            parts.push(`Reservation: ${reservationSearchTerm.trim()}`);
        }
        if (effectiveShowBuildingGrouping) {
            parts.push('Buildings grouped');
        }
        if (!showUnassignedReservations) {
            parts.push('Unassigned hidden');
        }
        return parts.join(' · ');
    }, [viewMode, getFilteredRooms, selectedStatusFilter, selectedServiceStatusFilter, selectedRoomTypeFilter, uniqueRoomTypeOptions, reservationSearchTerm, monitorShowDate, effectiveShowBuildingGrouping, showUnassignedReservations]);

    // Group rooms by status for the status-based view
    const getRoomsGroupedByStatus = () => {
        const statusGroups = {};

        getFilteredRooms.forEach(floor => {
            floor.rooms.forEach(room => {
                const status = room.status_display_text || 'Unknown';
                if (!statusGroups[status]) {
                    statusGroups[status] = {};
                }

                // Group by building + floor within each status
                const floorKey = `${getMonitorBuildingKey(floor)}|${floor.floor}`;
                if (!statusGroups[status][floorKey]) {
                    statusGroups[status][floorKey] = {
                        floor: floor.floor,
                        buildings_id: floor.buildings_id ?? null,
                        building_name: floor.building_name ?? null,
                        rooms: []
                    };
                }
                statusGroups[status][floorKey].rooms.push({
                    ...room,
                    floorNumber: floor.floor
                });
            });
        });

        // Convert to array and sort by status name, then by floor number
        return Object.entries(statusGroups)
            .map(([status, floorGroups]) => ({
                status,
                floors: Object.values(floorGroups)
                    .map((floorGroup) => ({
                        floor: parseInt(floorGroup.floor),
                        buildings_id: floorGroup.buildings_id,
                        building_name: floorGroup.building_name,
                        rooms: floorGroup.rooms.sort((a, b) => a.roomNumber - b.roomNumber)
                    }))
                    .sort((a, b) => b.floor - a.floor) // Sort floors in descending order
            }))
            .sort((a, b) => a.status.localeCompare(b.status));
    };

    // Unassigned: API may omit room_number key; use merged row + alternate assignment fields
    const getUnassignedReservations = useMemo(() => {
        return reservationsForReservationFilter.filter((reservation) => isReservationUnassignedForMonitor(reservation));
    }, [reservationsForReservationFilter]);

    // Room types still present after status / service-status / room-type display filters.
    // Used so reservation badges in Reservations View stay in sync with the filtered room set.
    const roomTypeKeysFromFilteredRooms = useMemo(() => {
        const keys = new Set();
        getFilteredRooms.forEach((floor) => {
            floor.rooms.forEach((room) => {
                const key = getRoomTypeFilterKey(room);
                if (key) keys.add(key);
            });
        });
        return keys;
    }, [getFilteredRooms]);

    const hasRoomAttributeDisplayFilter =
        selectedStatusFilter !== 'all' || selectedServiceStatusFilter !== 'all';

    // Display Filters apply to rooms AND to unassigned reservation badges on listed views
    const reservationsForReservationsView = useMemo(() => {
        return reservationsForReservationFilter.filter((reservation) => {
            const roomTypeKey = getRoomTypeFilterKey(reservation);

            if (selectedRoomTypeFilter !== 'all' && roomTypeKey !== selectedRoomTypeFilter) {
                return false;
            }

            // Status / service status are room attributes: keep reservations whose room type
            // still has rooms after those filters (e.g. Out Of Order → only types with OOO rooms).
            if (hasRoomAttributeDisplayFilter && !roomTypeKeysFromFilteredRooms.has(roomTypeKey)) {
                return false;
            }

            return true;
        });
    }, [
        reservationsForReservationFilter,
        selectedRoomTypeFilter,
        hasRoomAttributeDisplayFilter,
        roomTypeKeysFromFilteredRooms
    ]);

    const getUnassignedReservationsForListedViews = useMemo(() => {
        return reservationsForReservationsView.filter((reservation) =>
            isReservationUnassignedForMonitor(reservation)
        );
    }, [reservationsForReservationsView]);

    // Get rooms for Arrivals & Departures views (checkin_today, checkout_today, stayover, or turnover)
    const getArrivalsDeparturesRooms = useMemo(() => {
        const filterRooms = (predicate) => {
            return getFilteredRooms
                .map((floor) => ({
                    ...floor,
                    rooms: floor.rooms.filter(predicate)
                }))
                .filter((floor) => floor.rooms.length > 0);
        };

        // Require a reservation identity so orphaned room flags (e.g. checkin_today=1 with null confirmation) don't leak in.
        const hasValidReservationIdentity = (obj) => {
            if (!obj || typeof obj !== 'object') return false;
            const confirmation = obj.confirmation_number;
            const reservationId = obj.reservations_id;
            const hasConfirmation = confirmation != null && String(confirmation).trim() !== '';
            const hasReservationId = reservationId != null && String(reservationId).trim() !== '';
            return hasConfirmation || hasReservationId;
        };
        const roomHasAssignedReservation = (room) => {
            if (hasValidReservationIdentity(room)) return true;
            return Array.isArray(room?.confirmation_numbers) &&
                room.confirmation_numbers.some((c) => hasValidReservationIdentity(c));
        };
        // Use truthy checks so both boolean (true/false) and numeric (1/0) from API work
        // Count confirmation_numbers entries with checkin_today/checkout_today even when the PMS omits
        // confirmation_number / reservations_id (API may still send today flags per reservation segment).
        const hasCheckinToday = (room) => {
            if (!room) return false;
            if (
                Array.isArray(room.confirmation_numbers) &&
                room.confirmation_numbers.some((c) => c && typeof c === 'object' && !!c.checkin_today)
            ) {
                return true;
            }
            return (
                roomHasAssignedReservation(room) &&
                !!room.checkin_today
            );
        };
        const hasCheckoutToday = (room) => {
            if (!room) return false;
            if (
                Array.isArray(room.confirmation_numbers) &&
                room.confirmation_numbers.some((c) => c && typeof c === 'object' && !!c.checkout_today)
            ) {
                return true;
            }
            return (
                roomHasAssignedReservation(room) &&
                !!room.checkout_today
            );
        };

        const isCheckingInRoom = (room) =>
            hasCheckinToday(room) && !isRoomOccupied(room?.occupied);
        const isCheckedInRoom = (room) =>
            hasCheckinToday(room) && isRoomOccupied(room?.occupied);
        const hasConfirmationNumber = (room) => roomHasAssignedReservation(room);
        const isStayoverRoom = (room) =>
            hasConfirmationNumber(room) && !hasCheckinToday(room) && !hasCheckoutToday(room);
        const turnoverSegments = (room) =>
            Array.isArray(room?.confirmation_numbers)
                ? room.confirmation_numbers.filter((c) => c && typeof c === 'object')
                : [];
        const hasRealCrossReservationTurnover = (room) => {
            const segs = turnoverSegments(room);
            return (
                segs.length >= 2 &&
                segs.some((c) => !!c.checkout_today) &&
                segs.some((c) => !!c.checkin_today)
            );
        };
        const isTurnoverRoom = (room) => {
            if (!hasCheckinToday(room) || !hasCheckoutToday(room)) return false;
            const segs = turnoverSegments(room);
            if (
                segs.length === 1 &&
                !!segs[0].checkin_today &&
                !!segs[0].checkout_today
            ) {
                return false;
            }
            const topBoth = !!room.checkin_today && !!room.checkout_today;
            if (!topBoth) return true;
            return hasRealCrossReservationTurnover(room);
        };
        const isShowAllRoom = (room) =>
            isCheckingInRoom(room) ||
            isCheckedInRoom(room) ||
            isStayoverRoom(room) ||
            hasCheckoutToday(room) ||
            isTurnoverRoom(room);

        if (arrivalsDeparturesFilter === 'show_all') {
            return filterRooms(isShowAllRoom);
        }
        if (arrivalsDeparturesFilter === 'checkin') {
            return filterRooms(isCheckingInRoom);
        }
        if (arrivalsDeparturesFilter === 'checked_in') {
            return filterRooms(isCheckedInRoom);
        }
        if (arrivalsDeparturesFilter === 'checkout') {
            return filterRooms(hasCheckoutToday);
        }
        if (arrivalsDeparturesFilter === 'stayover') {
            return filterRooms(isStayoverRoom);
        }
        return filterRooms(isTurnoverRoom);
    }, [getFilteredRooms, arrivalsDeparturesFilter]);

    // Unassigned reservations filtered by current Arrivals & Departures view only:
    // - Checking In: checkin_today / check_in_today, or arrival date today if flags missing (same idea as turnover)
    // - Checking Out: unassigned with departure (check-out) today
    // - Stayovers: unassigned in-house (arrival before today, departure after today)
    // - Turning Over Today: unassigned with arrival today OR departure today (not both flags on one reservation)
    const getUnassignedForArrivalsDepartures = useMemo(() => {
        const today = moment().startOf('day');
        return getUnassignedReservationsForListedViews.filter((reservation) => {
            const row = reservationRowForDates(reservation);
            const arrival = getReservationArrivalMoment(reservation);
            const departure = getReservationDepartureMoment(reservation);

            const matchesCheckin = (() => {
                if (isRoomOccupied(row.occupied)) {
                    return false;
                }
                return (
                    reservationHasCheckinTodayForMonitor(reservation) ||
                    (!!arrival && arrival.isSame(moment(), 'day'))
                );
            })();
            const matchesCheckout = (() => {
                if (reservationHasCheckoutTodayForMonitor(reservation)) {
                    return true;
                }
                return !!(departure && departure.isSame(moment(), 'day'));
            })();
            const matchesStayover = !!(
                arrival &&
                departure &&
                arrival.isBefore(today, 'day') &&
                departure.isAfter(today, 'day')
            );
            const matchesTurnover = (() => {
                const checkinToday =
                    reservationHasCheckinTodayForMonitor(reservation) ||
                    (!!arrival && arrival.isSame(moment(), 'day'));
                const checkoutToday =
                    reservationHasCheckoutTodayForMonitor(reservation) ||
                    (!!departure && departure.isSame(moment(), 'day'));
                if (checkinToday && checkoutToday) return false;
                return checkinToday || checkoutToday;
            })();

            if (arrivalsDeparturesFilter === 'show_all') {
                return matchesCheckin;
            }
            if (arrivalsDeparturesFilter === 'checkin') {
                return matchesCheckin;
            }
            if (arrivalsDeparturesFilter === 'checkout') {
                return matchesCheckout;
            }
            if (arrivalsDeparturesFilter === 'stayover') {
                return matchesStayover;
            }
            if (arrivalsDeparturesFilter === 'turnover') {
                return matchesTurnover;
            }
            return false;
        });
    }, [getUnassignedReservationsForListedViews, arrivalsDeparturesFilter]);

    // Get active rooms (rooms with initials - being worked on by attendants/inspectors)
    const getActiveRooms = () => {
        const activeStatusGroups = {};

        getFilteredRooms.forEach(floor => {
            floor.rooms.forEach(room => {
                // Only include rooms that have cart_resource_initials (being worked on)
                if (room.cart_resource_initials) {
                    const status = room.status_display_text || 'Unknown';
                    if (!activeStatusGroups[status]) {
                        activeStatusGroups[status] = {};
                    }

                    // Group by building + floor within each status
                    const floorKey = `${getMonitorBuildingKey(floor)}|${floor.floor}`;
                    if (!activeStatusGroups[status][floorKey]) {
                        activeStatusGroups[status][floorKey] = {
                            floor: floor.floor,
                            buildings_id: floor.buildings_id ?? null,
                            building_name: floor.building_name ?? null,
                            rooms: []
                        };
                    }
                    activeStatusGroups[status][floorKey].rooms.push({
                        ...room,
                        floorNumber: floor.floor
                    });
                }
            });
        });

        // Convert to array and sort by status name, then by floor number
        return Object.entries(activeStatusGroups)
            .map(([status, floorGroups]) => ({
                status,
                floors: Object.values(floorGroups)
                    .map((floorGroup) => ({
                        floor: parseInt(floorGroup.floor),
                        buildings_id: floorGroup.buildings_id,
                        building_name: floorGroup.building_name,
                        rooms: floorGroup.rooms.sort((a, b) => a.roomNumber - b.roomNumber)
                    }))
                    .sort((a, b) => b.floor - a.floor) // Sort floors in descending order
            }))
            .sort((a, b) => a.status.localeCompare(b.status));
    };

    const getRoomStatus = async (isInitialCall = false, showDateOverride) => {
        const payload = {
            properties_id: selectedLoginData?.properties_id,
            show_date: showDateOverride ?? monitorShowDateRef.current,
        };

        if (isInitialCall) {
            setLoading(true); // Set loading true for the initial call
        }

        try {
            const res = await nbrApi.post('/getroomstatuses', payload);

            if (res?.data) {
                setRoomStatusesMeta((prev) => {
                    const picked = pickPmsPropertiesIdFromRoomStatusResponse(res.data);
                    const nextPms =
                        picked != null ? picked : prev?.pms_properties_id ?? null;
                    return { pms_properties_id: nextPms };
                });

                const roomsData = res?.data?.rooms_array || [];
                const reservationsData = res?.data?.reservations_array || [];

                const convertedData = convertDataToFloorStructure(roomsData);
                const enrichedFromReservations = enrichMonitorRoomsWithAssignedReservations(
                    convertedData,
                    reservationsData,
                    payload.show_date
                );
                const withInitials = applySampleCartInitials(enrichedFromReservations);
                const withProgressAndBadges = applySampleProgressAndBadges(withInitials);
                const withTurnoverSample = applySampleTurnoverRooms(withProgressAndBadges);
                const withDummyCheckinRooms = applyDummyMonitorCheckinRooms(withTurnoverSample);
                const withDummyInspectorRoom = applyDummyMonitorInspectorRoom(withDummyCheckinRooms);
                const withDummyTwoBadgesRoom = applyDummyMonitorTwoBadgesRoom(withDummyInspectorRoom);
                const withDummyOneBadgeInTimeRoom = applyDummyMonitorOneBadgeInTimeRoom(withDummyTwoBadgesRoom);
                const withDummyProgressBar = applyDummyMonitorProgressBar(withDummyOneBadgeInTimeRoom);
                const withDummyCartIcon = applyDummyMonitorCartIcon(withDummyProgressBar);
                setRooms(withDummyCartIcon);

                setReservations(
                    applyDummyMonitorCheckinReservations(
                        reservationsData,
                        res.data,
                        selectedLoginData?.properties_id
                    )
                );

                const roomAssignmentsData = res?.data?.room_assignment_array || [];
                setRoomAssignments(roomAssignmentsData);
            }
        } catch (error) {
            console.error("Error fetching room status:", error);
        } finally {
            if (isInitialCall) {
                setLoading(false); // Set loading false after the initial call
            }
        }
    };

    const applyMonitorShowDate = (nextDate) => {
        if (!nextDate) return;
        setMonitorShowDate(nextDate);
        monitorShowDateRef.current = nextDate;
        getRoomStatus(true, nextDate);
    };

    const shiftMonitorShowDate = (dayDelta) => {
        const nextDate = moment(monitorShowDate, 'YYYY-MM-DD')
            .add(dayDelta, 'days')
            .format('YYYY-MM-DD');
        applyMonitorShowDate(nextDate);
    };

    useEffect(() => {
        let intervalId;
        let isInitialCall = true; // Flag for the first call

        const startRefreshing = () => {
            if (isInitialCall) {
                getRoomStatus(true).finally(() => {
                    isInitialCall = false; // Update the flag after the first call
                });
            } else {
                getRoomStatus(); // No loading state for subsequent calls
            }

            // Start refreshing data every 5 seconds
            if (!intervalId) {
                intervalId = setInterval(() => {
                    if (document.visibilityState === 'visible') {
                        getRoomStatus(); // Subsequent calls without loading state
                    }
                }, 5000);
            }
        };

        const stopRefreshing = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        if (document.visibilityState === 'visible') {
            startRefreshing();
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                startRefreshing();
            } else {
                stopRefreshing();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stopRefreshing();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Handle confirmation badge click (second arg from room status context: assign vs show)
    const handleConfirmationBadgeClick = (confirmationNumber, assignmentContext) => {
        setSelectedConfirmationNumber(confirmationNumber);
        setSelectedAssignmentContext(
            assignmentContext === 'assign' || assignmentContext === 'show' ? assignmentContext : null
        );
        setShowReservationModal(true);
    };

    const handleCompactBadgeClick = (conf) => {
        const { confirmationForClick } = getMonitorBadgeLabel(conf, badgeDisplayMode);
        handleConfirmationBadgeClick(confirmationForClick, 'show');
    };

    const getFloorDoorCardLayoutSlots = (rooms) => ({
        badgeSlotRowCount: getFloorMaxBadgeRowCount(rooms, badgeDisplayMode),
        footerSlotHeightPx: getFloorMaxBelowBlockHeightPx(rooms, { compact: useCompactRoomCells })
    });

    const renderMonitorRoomCell = (room, badgeSlotRowCount = null, footerSlotHeightPx = null) =>
        useCompactRoomCells ? (
            <CompactRoomMonitorCellWithDoorHover
                key={room.roomNumber}
                room={room}
                badgeDisplayMode={badgeDisplayMode}
                onBadgeClick={handleCompactBadgeClick}
                onConfirmationClick={handleConfirmationBadgeClick}
                footerSlotHeightPx={footerSlotHeightPx}
            />
        ) : (
            <MonitorDoorCardV2
                key={room.roomNumber}
                data={room}
                roomNumber={room.roomNumber}
                status={room.status}
                statusColor={room.statusColor}
                isDnd={room.isDnd}
                show_icon={room.show_icon}
                icon_blink_interval={room.icon_blink_interval}
                isMonitor={true}
                onConfirmationClick={handleConfirmationBadgeClick}
                badgeDisplayMode={badgeDisplayMode}
                badgeSlotRowCount={badgeSlotRowCount}
                footerSlotHeightPx={footerSlotHeightPx}
            />
        );

    /** Same floor band as Floor View — in-flow cart/inspector icons + aligned badge/footer slots. */
    const renderMonitorFloorRow = (floor, rowKey = getMonitorFloorRowKey(floor)) => {
        const doorCardLayout = getFloorDoorCardLayoutSlots(floor.rooms);
        return (
            <div
                key={rowKey}
                className="monitor-floor-row monitor-floor-row--door-cards"
                style={{ boxShadow: 'rgba(33, 35, 38, 0.1) 0px 10px 10px -10px' }}
            >
                <div className="d-lg-flex align-items-start justify-content-start gap-3">
                    <h4
                        className="mb-0 text-start d-flex align-items-center justify-content-center flex-shrink-0"
                        style={floorLabelStyle}
                    >
                        {floor.floor}
                    </h4>
                    <div className={`w-full d-flex flex-wrap gap-1${monitorFloorRoomsRowClass}`}>
                        {floor.rooms.map((room) =>
                            renderMonitorRoomCell(
                                room,
                                doorCardLayout.badgeSlotRowCount,
                                doorCardLayout.footerSlotHeightPx
                            )
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderMonitorBuildingGroupHeader = (building, roomCount) => (
        <div className="monitor-building-group__header mb-3">
            <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-primary fw-bold">
                    <i className="bi bi-building me-2"></i>
                    {building.building_name}
                </h5>
                <span className="badge bg-secondary fs-6 px-3 py-2">
                    {roomCount} Room{roomCount !== 1 ? 's' : ''}
                </span>
            </div>
        </div>
    );

    const renderMonitorFloorsList = (floors, keyPrefix = '') => {
        const sortedFloors = sortMonitorFloorsDesc(floors);

        if (!effectiveShowBuildingGrouping) {
            return sortedFloors.map((floor) =>
                renderMonitorFloorRow(floor, `${keyPrefix}${getMonitorFloorRowKey(floor)}`)
            );
        }

        return groupMonitorFloorsByBuilding(sortedFloors).map((building) => {
            const buildingKey = building.buildings_id ?? building.building_name;
            const roomCount = building.floors.reduce((total, floor) => total + floor.rooms.length, 0);

            return (
                <div
                    key={`${keyPrefix}building-${buildingKey}`}
                    className="monitor-building-group mb-4"
                >
                    {renderMonitorBuildingGroupHeader(building, roomCount)}
                    {building.floors.map((floor) =>
                        renderMonitorFloorRow(
                            floor,
                            `${keyPrefix}${buildingKey}-${getMonitorFloorRowKey(floor)}`
                        )
                    )}
                </div>
            );
        });
    };

    // Helper function to render confirmation badge (matching MonitorCard format)
    const renderConfirmationBadge = (conf, isUnassigned = false) => {
        const pres = getMonitorStandaloneBadgePresentation(conf, badgeDisplayMode, { isUnassigned });
        const confirmationNumber = typeof conf === 'object' && conf != null ? conf.confirmation_number : conf;
        const key = pres.confirmationForClick || confirmationNumber;
        const hoverHandlers = {
            onMouseEnter: (e) => {
                e.currentTarget.style.opacity = '0.8';
            },
            onMouseLeave: (e) => {
                e.currentTarget.style.opacity = '1';
            }
        };

        if (pres.kind === 'unassigned') {
            return (
                <div
                    key={key}
                    onClick={() =>
                        handleConfirmationBadgeClick(pres.confirmationForClick, 'assign')
                    }
                    className="text-center text-decoration-none"
                    style={getMonitorUnassignedBadgeOuterStyle()}
                    {...hoverHandlers}
                    title={pres.tooltip}
                >
                    <div
                        style={{
                            backgroundColor: MONITOR_BADGE_CHECKIN_COLOR,
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX,
                            lineHeight: 1.2,
                            padding: '2px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        <span
                            aria-hidden
                            style={{ fontSize: Math.max(7, MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX - 4), lineHeight: 1 }}
                        >
                            ▲
                        </span>
                        <span
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {pres.display}
                        </span>
                    </div>
                    <div
                        style={{
                            backgroundColor: '#fff',
                            color: MONITOR_UNASSIGNED_BADGE_ROOM_TYPE_COLOR,
                            fontWeight: 'bold',
                            fontSize: MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX + 3,
                            lineHeight: 1.2,
                            padding: '5px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {pres.roomTypeCode}
                    </div>
                </div>
            );
        }

        return (
            <div
                key={key}
                onClick={() =>
                    handleConfirmationBadgeClick(
                        pres.confirmationForClick,
                        isUnassigned ? 'assign' : 'show'
                    )
                }
                className="text-center text-decoration-none"
                style={getMonitorStandaloneBadgeShellStyle(pres)}
                {...hoverHandlers}
                title={pres.tooltip}
            >
                {pres.display}
            </div>
        );
    };

    /** Floor-style band above building groups: unassigned badges for the current view filters. */
    const renderUnassignedReservationsFloorRow = (reservationsList) => {
        if (!showUnassignedReservations || !reservationsList || reservationsList.length === 0) {
            return null;
        }

        return (
            <div className="card shadow-sm border-0 bg-white monitor-grouped-room-card monitor-unassigned-floor mb-3">
                <div className="row">
                    <div className="col-12 mx-0">
                        <div className="card-body py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 text-primary fw-bold">
                                    <i className="bi bi-calendar-x me-2"></i>
                                    Unassigned Reservations
                                </h5>
                                <span className="badge bg-secondary fs-6 px-3 py-2">
                                    {reservationsList.length} Reservation{reservationsList.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <hr className="monitor-unassigned-floor__divider" />
                <div className="monitor-unassigned-floor__badges monitor-standalone-badge-stack">
                    {reservationsList.map((reservation, index) => {
                        const conf = typeof reservation === 'object'
                            ? reservation
                            : { confirmation_number: reservation };
                        const key = conf.confirmation_number || conf.reservations_id || index;
                        return (
                            <div key={key}>
                                {renderConfirmationBadge(conf, true)}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };


    const filterSelectShellStyle = (width) => ({
        width,
        maxWidth: '100%',
        minWidth: 0,
        borderRadius: '12px',
        border: '2px solid #e9ecef',
        transition: 'all 0.3s ease'
    });

    const renderDisplayFilterSelects = ({ showRoomCount = true } = {}) => (
        <div className="monitor-display-filters">
            <div className="monitor-display-filters__field">
                <label htmlFor="statusFilter" className="form-label fw-bold text-primary mb-2">
                    <i className="bi bi-funnel me-2"></i>
                    Filter by Status
                </label>
                <select
                    id="statusFilter"
                    className="form-select form-select-lg monitor-floor-filter-control shadow-sm border-0 bg-light text-primary bg-gray-100"
                    value={selectedStatusFilter}
                    onChange={(e) => setSelectedStatusFilter(e.target.value)}
                    style={filterSelectShellStyle(statusFilterSelectWidth)}
                >
                    <option value="all" className="fw-bold">🔄 All Statuses</option>
                    {selectedStatusFilter !== 'all' && !uniqueStatusOptions.includes(selectedStatusFilter) && (
                        <option value={selectedStatusFilter}>{selectedStatusFilter}</option>
                    )}
                    {uniqueStatusOptions.map((status) => (
                        <option key={status} value={status} className="fw-medium">
                            {status}
                        </option>
                    ))}
                </select>
            </div>
            <div className="monitor-display-filters__field">
                <label htmlFor="serviceStatusFilter" className="form-label fw-bold text-primary mb-2">
                    <i className="bi bi-funnel me-2"></i>
                    Filter by Service Status
                </label>
                <select
                    id="serviceStatusFilter"
                    className="form-select form-select-lg monitor-floor-filter-control shadow-sm border-0 bg-light text-primary bg-gray-100"
                    value={selectedServiceStatusFilter}
                    onChange={(e) => setSelectedServiceStatusFilter(e.target.value)}
                    style={filterSelectShellStyle(serviceStatusFilterSelectWidth)}
                >
                    <option value="all" className="fw-bold">🔄 All Service Statuses</option>
                    {selectedServiceStatusFilter !== 'all' && !uniqueServiceStatusOptions.includes(selectedServiceStatusFilter) && (
                        <option value={selectedServiceStatusFilter}>
                            {formatServiceStatusDisplay(selectedServiceStatusFilter)}
                        </option>
                    )}
                    {uniqueServiceStatusOptions.map((serviceStatus) => (
                        <option key={serviceStatus} value={serviceStatus} className="fw-medium">
                            {formatServiceStatusDisplay(serviceStatus)}
                        </option>
                    ))}
                </select>
            </div>
            <div className="monitor-display-filters__field">
                <label htmlFor="roomTypeFilter" className="form-label fw-bold text-primary mb-2">
                    <i className="bi bi-funnel me-2"></i>
                    Filter by Room Type
                </label>
                <select
                    id="roomTypeFilter"
                    className="form-select form-select-lg monitor-floor-filter-control shadow-sm border-0 bg-light text-primary bg-gray-100"
                    value={selectedRoomTypeFilter}
                    onChange={(e) => setSelectedRoomTypeFilter(e.target.value)}
                    style={filterSelectShellStyle(roomTypeFilterSelectWidth)}
                >
                    <option value="all" className="fw-bold">🔄 All Room Types</option>
                    {selectedRoomTypeFilter !== 'all' &&
                        !uniqueRoomTypeOptions.some((opt) => opt.key === selectedRoomTypeFilter) && (
                        <option value={selectedRoomTypeFilter}>{selectedRoomTypeFilter}</option>
                    )}
                    {uniqueRoomTypeOptions.map((roomType) => (
                        <option key={roomType.key} value={roomType.key} className="fw-medium">
                            {roomType.label}
                        </option>
                    ))}
                </select>
            </div>
            {showRoomCount && (
                <div className="monitor-display-filters__count">
                    <span className="badge bg-primary fs-6 px-3 py-2">
                        {getFilteredRooms.reduce((total, floor) => total + floor.rooms.length, 0)} Rooms
                    </span>
                </div>
            )}
        </div>
    );

    const renderFloorFilterCard = () => (
        <div className="row mb-0 mt-2">
            <div className="col-12">
                <div className="card shadow-sm border-0">
                    <div className="card-body">
                        {renderDisplayFilterSelects()}
                    </div>
                </div>
            </div>
        </div>
    );

    // Render floor-based view
    const renderFloorView = () => {
        const unassignedRow = renderUnassignedReservationsFloorRow(getUnassignedReservationsForListedViews);

        if (getFilteredRooms.length === 0) {
            return (
                <>
                    {unassignedRow}
                    <div className="card shadow-sm border-0 bg-white mt-3">
                        <div className="card-body text-center py-5">
                            <i className="bi bi-search text-muted" style={{ fontSize: '3rem' }}></i>
                            <h5 className="text-muted mt-3">No Matching Rooms</h5>
                            <p className="text-muted mb-0">
                                Try another status, service status, room type, confirmation number, or guest last name.
                            </p>
                        </div>
                    </div>
                </>
            );
        }

        return (
            <>
                {unassignedRow}
                {renderMonitorFloorsList(getFilteredRooms)}
            </>
        );
    };

    // Render status-based view
    const renderStatusView = () => {
        const statusGroups = getRoomsGroupedByStatus();

        return (
            <>
                {renderUnassignedReservationsFloorRow(getUnassignedReservationsForListedViews)}
                <div className="flex-grow-1">
                        {/* Status Groups */}
                        {statusGroups.map((group, groupIndex) => (
                            <div key={group.status} className="card shadow-sm border-0 bg-white monitor-grouped-room-card">
                                {/* Status Header */}
                                <div className="row mb-3">
                                    <div className="col-12 mx-0">
                                        <div className="">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0 text-primary fw-bold">
                                                        <i className="bi bi-tag me-2"></i>
                                                        {group.status}
                                                    </h5>
                                                    <span className="badge bg-secondary fs-6 px-3 py-2">
                                                        {group.floors.reduce((total, floor) => total + floor.rooms.length, 0)} Room{group.floors.reduce((total, floor) => total + floor.rooms.length, 0) !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Floor Rows within Status */}
                                {renderMonitorFloorsList(group.floors, `${group.status}-`)}
                            </div>
                        ))}
                </div>
            </>
        );
    };

    // Render active rooms view
    const renderActiveRoomsView = () => {
        const activeStatusGroups = getActiveRooms();
        const totalRooms = activeStatusGroups.reduce((total, group) =>
            total + group.floors.reduce((floorTotal, floor) => floorTotal + floor.rooms.length, 0), 0);

        const unassignedRow = renderUnassignedReservationsFloorRow(getUnassignedReservationsForListedViews);

        // If no active rooms, still show matching unassigned badges when present
        if (activeStatusGroups.length === 0) {
            return (
                <>
                    {unassignedRow}
                    <div className="row">
                        <div className="col-12">
                            <div className="card shadow-sm border-0" style={{ backgroundColor: '#fff' }}>
                                <div className="card-body text-center py-5">
                                    <i className="bi bi-info-circle text-muted" style={{ fontSize: '3rem' }}></i>
                                    <h5 className="text-muted mt-3">No Active Rooms</h5>
                                    <p className="text-muted mb-0">
                                        There are currently no rooms being worked on by room attendants or inspectors.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            );
        }

        return (
            <>
                {/* Summary Card */}
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0 bg-gradient-success text-white">
                            <div className="card-body text-center">
                                <h4 className="mb-2">
                                    <i className="bi bi-lightbulb me-2"></i>
                                    Active Rooms
                                </h4>
                                <p className="mb-0 fs-5 text-black">
                                    {activeStatusGroups.length} Status Types • {totalRooms} Total Rooms
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {unassignedRow}
                <div className="flex-grow-1">
                        {/* Status Groups */}
                        {activeStatusGroups.map((group, groupIndex) => (
                            <div key={group.status} className="card shadow-sm border-0 bg-white monitor-grouped-room-card">
                                {/* Status Header */}
                                <div className="row mb-3">
                                    <div className="col-12 mx-0">
                                        <div className="">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0 text-success fw-bold">
                                                        <i className="bi bi-lightbulb me-2"></i>
                                                        {group.status}
                                                    </h5>
                                                    <span className="badge bg-secondary fs-6 px-3 py-2">
                                                        {group.floors.reduce((total, floor) => total + floor.rooms.length, 0)} Room{group.floors.reduce((total, floor) => total + floor.rooms.length, 0) !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Floor Rows within Status */}
                                {renderMonitorFloorsList(group.floors, `${group.status}-`)}
                            </div>
                        ))}
                </div>
            </>
        );
    };

    const renderArrivalsDeparturesSubfilter = () => {
        const currentFilter = ARRIVALS_DEPARTURES_FILTER_LABELS[arrivalsDeparturesFilter];

        return (
            <div className="monitor-index-arrivals-subfilter-wrap">
                <div className="row mb-0 monitor-arrivals-subfilter">
                    <div className="col-12">
                        <div className="card shadow-sm border-0 mb-0">
                            <div className="card-body py-2">
                                <label className="form-label fw-bold text-primary mb-2">
                                    <i className="bi bi-calendar-event me-2"></i>
                                    Show
                                </label>
                                <div className="btn-group w-100 flex-wrap" role="group">
                                    {ARRIVALS_DEPARTURES_FILTER_KEYS.map((key) => {
                                        const { label, icon, color } = ARRIVALS_DEPARTURES_FILTER_LABELS[key];
                                        const isActive = arrivalsDeparturesFilter === key;
                                        return (
                                            <React.Fragment key={key}>
                                                <input
                                                    type="radio"
                                                    className="btn-check"
                                                    name="arrivalsDeparturesFilter"
                                                    id={`filter_${key}`}
                                                    value={key}
                                                    checked={isActive}
                                                    onChange={(e) =>
                                                        setArrivalsDeparturesFilter(e.target.value)
                                                    }
                                                />
                                                <label
                                                    className={`btn btn-outline-${color}`}
                                                    htmlFor={`filter_${key}`}
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    <i className={`bi ${icon} me-2`}></i>
                                                    {label}
                                                </label>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                                <p className="text-muted small mb-0 mt-2">{currentFilter.description}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render Arrivals & Departures view (Checking In, Stayovers, Checking Out, Turning Over Today)
    const renderArrivalsDeparturesView = () => {
        const filteredFloors = getArrivalsDeparturesRooms;
        const totalRooms = filteredFloors.reduce((total, floor) => total + floor.rooms.length, 0);

        const currentFilter = ARRIVALS_DEPARTURES_FILTER_LABELS[arrivalsDeparturesFilter];
        const unassignedForView = getUnassignedForArrivalsDepartures;
        const hasUnassignedForView = showUnassignedReservations && unassignedForView.length > 0;

        if (filteredFloors.length === 0 && !hasUnassignedForView) {
            return (
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0" style={{ backgroundColor: '#fff' }}>
                            <div className="card-body text-center py-5">
                                <i className={`bi ${currentFilter.icon} text-muted`} style={{ fontSize: '3rem' }}></i>
                                <h5 className="text-muted mt-3">No {currentFilter.label} Rooms</h5>
                                <p className="text-muted mb-0">{currentFilter.description}</p>
                                <p className="text-muted small mt-2">Try another filter above.</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <>
                {/* Summary Card */}
                <div className="row mb-3">
                    <div className="col-12">
                        <div className={`card shadow-sm border-0 bg-gradient-${currentFilter.color} text-white`}>
                            <div className="card-body text-center">
                                <h4 className="mb-2">
                                    <i className={`bi ${currentFilter.icon} me-2`}></i>
                                    {currentFilter.label}
                                </h4>
                                <p className="mb-0 fs-5 text-black">
                                    {totalRooms} Room{totalRooms !== 1 ? 's' : ''} • {filteredFloors.length} Floor{filteredFloors.length !== 1 ? 's' : ''}
                                    {totalRooms === 0 && hasUnassignedForView && (
                                        <span className="d-block small text-dark mt-2 fw-normal">
                                            Unassigned reservations for this view are listed above; no rooms match this filter yet.
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {renderUnassignedReservationsFloorRow(unassignedForView)}
                <div className="flex-grow-1">
                    {filteredFloors.length === 0 ? (
                        <div className="card shadow-sm border-0 bg-white">
                            <div className="card-body text-center py-5 text-muted">
                                <p className="mb-0">
                                    No rooms match this filter yet. Assign from Unassigned Reservations above, or choose another
                                    sub-filter.
                                </p>
                            </div>
                        </div>
                    ) : (
                        renderMonitorFloorsList(filteredFloors)
                    )}
                </div>
            </>
        );
    };

    const renderUnassignedReservationsToggle = () => (
        <div className="monitor-toolbar-item">
            <label className="monitor-toolbar-label form-label fw-bold text-primary">
                <i className="bi bi-calendar-x me-1" aria-hidden />
                Unassigned reservations
            </label>
            <div className="btn-group monitor-toolbar-control" role="group" aria-label="Unassigned reservations">
                <input
                    type="radio"
                    className="btn-check"
                    name="unassignedReservationsVisibility"
                    id="unassignedReservationsShow"
                    checked={showUnassignedReservations}
                    onChange={() => setShowUnassignedReservations(true)}
                />
                <label
                    className="btn btn-outline-secondary"
                    htmlFor="unassignedReservationsShow"
                >
                    Show
                </label>
                <input
                    type="radio"
                    className="btn-check"
                    name="unassignedReservationsVisibility"
                    id="unassignedReservationsHide"
                    checked={!showUnassignedReservations}
                    onChange={() => setShowUnassignedReservations(false)}
                />
                <label
                    className="btn btn-outline-secondary"
                    htmlFor="unassignedReservationsHide"
                >
                    Hide
                </label>
            </div>
        </div>
    );

    const renderBuildingGroupingToggle = () => {
        if (!hasBuildingData) return null;

        return (
            <div className="monitor-toolbar-item">
                <label className="monitor-toolbar-label form-label fw-bold text-primary">
                    <i className="bi bi-building me-1" aria-hidden />
                    Building grouping
                </label>
                <div className="btn-group monitor-toolbar-control" role="group" aria-label="Building grouping">
                    <input
                        type="radio"
                        className="btn-check"
                        name="buildingGrouping"
                        id="buildingGroupingShow"
                        checked={showBuildingGrouping}
                        onChange={() => setShowBuildingGrouping(true)}
                    />
                    <label
                        className="btn btn-outline-secondary"
                        htmlFor="buildingGroupingShow"
                    >
                        Show
                    </label>
                    <input
                        type="radio"
                        className="btn-check"
                        name="buildingGrouping"
                        id="buildingGroupingHide"
                        checked={!showBuildingGrouping}
                        onChange={() => setShowBuildingGrouping(false)}
                    />
                    <label
                        className="btn btn-outline-secondary"
                        htmlFor="buildingGroupingHide"
                    >
                        Hide
                    </label>
                </div>
            </div>
        );
    };

    return (
        loading ? (
            <FullPageLoader />
        ) : (
            <div
                className={`monitor-index-page${viewMode === 'floor' ? ' monitor-index-page--floor' : ''}${viewMode === 'arrivals_departures' ? ' monitor-index-page--arrivals-departures' : ''}${useCompactRoomCells ? ' monitor-index-page--compact-cells' : ''}`}
            >
                <div className="monitor-index-toolbar">
                    <div
                        className={`monitor-index-toolbar__header${monitorToolbarExpanded ? ' monitor-index-toolbar__header--expanded' : ''}`}
                    >
                        {!monitorToolbarExpanded && (
                            <div className="monitor-index-toolbar__summary text-muted small">
                                {monitorToolbarCollapsedHint}
                            </div>
                        )}
                        <div className="monitor-index-toolbar__actions">
                            <button
                                type="button"
                                className={`btn btn-sm flex-shrink-0 ${monitorToolbarExpanded ? 'btn-outline-secondary' : 'btn-primary'}`}
                                onClick={() => setMonitorToolbarExpanded((v) => !v)}
                                aria-expanded={monitorToolbarExpanded}
                                aria-controls="monitor-toolbar-controls"
                                id="monitor-toolbar-toggle"
                            >
                                <i
                                    className={`bi ${monitorToolbarExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}
                                    aria-hidden
                                />
                                {monitorToolbarExpanded ? 'Hide toolbar' : 'Show toolbar'}
                            </button>
                        </div>
                    </div>

                    {monitorToolbarExpanded && (
                        <div id="monitor-toolbar-controls">
                            {/* View Mode Toggle + badge label preference */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="card shadow-sm border-0 monitor-toolbar-preferences">
                                        <div className="card-body px-3 px-lg-4 py-3">
                                            <div className="d-flex flex-column flex-xl-row flex-wrap align-items-stretch align-items-xl-end justify-content-xl-between gap-3 gap-xl-4">
                                                <div className="d-flex flex-wrap align-items-end gap-3 gap-md-2 monitor-toolbar-cluster min-w-0 w-100">
                                                    <div className="monitor-toolbar-item monitor-toolbar-date-wrap">
                                                        <label htmlFor="monitorShowDate" className="monitor-toolbar-label form-label fw-bold text-primary">
                                                            Show date
                                                        </label>
                                                        <div className="monitor-toolbar-date-control monitor-toolbar-control">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary monitor-toolbar-date-step"
                                                                onClick={() => shiftMonitorShowDate(-1)}
                                                                aria-label="Previous day"
                                                            >
                                                                <i className="bi bi-chevron-left" aria-hidden />
                                                            </button>
                                                            <input
                                                                id="monitorShowDate"
                                                                type="date"
                                                                className="form-control monitor-toolbar-date-input"
                                                                value={monitorShowDate}
                                                                onChange={(e) => applyMonitorShowDate(e.target.value)}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary monitor-toolbar-date-step"
                                                                onClick={() => shiftMonitorShowDate(1)}
                                                                aria-label="Next day"
                                                            >
                                                                <i className="bi bi-chevron-right" aria-hidden />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="monitor-toolbar-item">
                                                        <label className="monitor-toolbar-label form-label fw-bold text-primary">
                                                            Show
                                                        </label>
                                                        <div className="btn-group monitor-toolbar-control" role="group">
                                                            <input
                                                                type="radio"
                                                                className="btn-check"
                                                                name="badgeDisplayMode"
                                                                id="badgeShowConfirmation"
                                                                value={BADGE_DISPLAY_CONFIRMATION}
                                                                checked={badgeDisplayMode === BADGE_DISPLAY_CONFIRMATION}
                                                                onChange={() => setBadgeDisplayMode(BADGE_DISPLAY_CONFIRMATION)}
                                                            />
                                                            <label className="btn btn-outline-secondary" htmlFor="badgeShowConfirmation">
                                                                <span className="d-none d-xl-inline">Confirmation Number</span>
                                                                <span className="d-inline d-xl-none">Conf #</span>
                                                            </label>
                                                            <input
                                                                type="radio"
                                                                className="btn-check"
                                                                name="badgeDisplayMode"
                                                                id="badgeShowGuest"
                                                                value={BADGE_DISPLAY_GUEST}
                                                                checked={badgeDisplayMode === BADGE_DISPLAY_GUEST}
                                                                onChange={() => setBadgeDisplayMode(BADGE_DISPLAY_GUEST)}
                                                            />
                                                            <label className="btn btn-outline-secondary" htmlFor="badgeShowGuest">
                                                                <span className="d-none d-md-inline">Guest Name</span>
                                                                <span className="d-inline d-md-none">Guest</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="monitor-toolbar-item">
                                                        <label className="monitor-toolbar-label form-label fw-bold text-primary">
                                                            Room layout
                                                        </label>
                                                        <div className="btn-group monitor-toolbar-control" role="group">
                                                            <input
                                                                type="radio"
                                                                className="btn-check"
                                                                name="roomCellLayout"
                                                                id="roomCellsDoor"
                                                                checked={!useCompactRoomCells}
                                                                onChange={() => setUseCompactRoomCells(false)}
                                                            />
                                                            <label className="btn btn-outline-secondary" htmlFor="roomCellsDoor">
                                                                Door cards
                                                            </label>
                                                            <input
                                                                type="radio"
                                                                className="btn-check"
                                                                name="roomCellLayout"
                                                                id="roomCellsCompact"
                                                                checked={useCompactRoomCells}
                                                                onChange={() => setUseCompactRoomCells(true)}
                                                            />
                                                            <label
                                                                className="btn btn-outline-secondary"
                                                                htmlFor="roomCellsCompact"
                                                                title="Status circles with reservation dots; click circle for full door"
                                                            >
                                                                Compact
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div className="monitor-toolbar-item monitor-toolbar-filter-wrap">
                                                        <label
                                                            htmlFor="reservationSearchFilter"
                                                            className="monitor-toolbar-label form-label fw-bold text-primary"
                                                        >
                                                            <span className="d-none d-md-inline">Filter by Reservation</span>
                                                            <span className="d-inline d-md-none">Reservation filter</span>
                                                        </label>
                                                        <input
                                                            id="reservationSearchFilter"
                                                            type="search"
                                                            className="form-control monitor-toolbar-reservation-search monitor-toolbar-control"
                                                            value={reservationSearchTerm}
                                                            onChange={(e) => setReservationSearchTerm(e.target.value)}
                                                            placeholder="Confirmation # or last name"
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    {renderUnassignedReservationsToggle()}
                                                    {renderBuildingGroupingToggle()}
                                                </div>
                                                <div className="monitor-toolbar-viewmode flex-shrink-0">
                                                    <label className="monitor-toolbar-label form-label fw-bold text-primary">
                                                        <i className="bi bi-eye me-2"></i>
                                                        View Mode
                                                    </label>
                                                    <div
                                                        className="btn-group flex-wrap monitor-toolbar-viewmode-btns"
                                                        role="group"
                                                    >
                                                            <input
                                                                type="radio"
                                                                className="btn-check"
                                                                name="viewMode"
                                                                id="floorView"
                                                                value="floor"
                                                                checked={viewMode === 'floor'}
                                                                onChange={(e) => setViewMode(e.target.value)}
                                                            />
                                                            <label className="btn btn-outline-primary" htmlFor="floorView">
                                                                <i className="bi bi-building me-1 me-sm-2"></i>
                                                                <span className="d-none d-sm-inline">Floor View</span>
                                                                <span className="d-inline d-sm-none">Floor</span>
                                                            </label>

                                                            <input
                                                                type="radio"
                                                                className="btn-check"
                                                                name="viewMode"
                                                                id="statusView"
                                                                value="status"
                                                                checked={viewMode === 'status'}
                                                                onChange={(e) => setViewMode(e.target.value)}
                                                            />
                                                            <label className="btn btn-outline-primary" htmlFor="statusView">
                                                                <i className="bi bi-tags me-1 me-sm-2"></i>
                                                                <span className="d-none d-sm-inline">Status View</span>
                                                                <span className="d-inline d-sm-none">Status</span>
                                                            </label>

                                                            {!isRoomopsRole && (
                                                                <>
                                                                    <input
                                                                        type="radio"
                                                                        className="btn-check"
                                                                        name="viewMode"
                                                                        id="activeView"
                                                                        value="active"
                                                                        checked={viewMode === 'active'}
                                                                        onChange={(e) => setViewMode(e.target.value)}
                                                                    />
                                                                    <label className="btn btn-outline-success" htmlFor="activeView">
                                                                        <i className="bi bi-lightbulb me-1 me-sm-2"></i>
                                                                        <span className="d-none d-sm-inline">Active Rooms</span>
                                                                        <span className="d-inline d-sm-none">Active</span>
                                                                    </label>
                                                                </>
                                                            )}

                                                            <input
                                                                type="radio"
                                                                className="btn-check"
                                                                name="viewMode"
                                                                id="arrivalsDeparturesView"
                                                                value="arrivals_departures"
                                                                checked={viewMode === 'arrivals_departures'}
                                                                onChange={(e) => setViewMode(e.target.value)}
                                                            />
                                                            <label className="btn btn-outline-warning" htmlFor="arrivalsDeparturesView">
                                                                <i className="bi bi-calendar-event me-1 me-sm-2"></i>
                                                                <span className="d-none d-md-inline">Arrivals &amp; Departures</span>
                                                                <span className="d-inline d-md-none">Arrivals</span>
                                                            </label>

                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-info monitor-toolbar-auto-assign"
                                                                disabled={!canBulkAutoAssign}
                                                                title={
                                                                    canBulkAutoAssign
                                                                        ? 'Open auto-assign reservations'
                                                                        : 'Select a property to auto-assign reservations'
                                                                }
                                                                onClick={() => setShowBulkRoomAssignmentModal(true)}
                                                            >
                                                                <i className="bi bi-calendar-check me-1 me-sm-2"></i>
                                                                <span className="d-none d-lg-inline">Auto-Assign Reservations</span>
                                                                <span className="d-none d-sm-inline d-lg-none">Auto-Assign</span>
                                                                <span className="d-inline d-sm-none">Auto-Assign</span>
                                                                {getUnassignedReservations.length > 0 && (
                                                                    <span
                                                                        className="badge bg-danger rounded-circle monitor-toolbar-auto-assign__count"
                                                                        title={`${getUnassignedReservations.length} unassigned`}
                                                                    >
                                                                        {getUnassignedReservations.length}
                                                                    </span>
                                                                )}
                                                            </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}

                </div>

                {monitorToolbarExpanded && (
                    <div className="monitor-index-floor-filter-wrap">{renderFloorFilterCard()}</div>
                )}

                {viewMode === 'arrivals_departures' && renderArrivalsDeparturesSubfilter()}

                <div className="monitor-index-scroll">
                    <div className="monitor-index-scroll-content">
                        {viewMode === 'status'
                            ? renderStatusView()
                            : viewMode === 'active'
                                ? renderActiveRoomsView()
                                : viewMode === 'arrivals_departures'
                                    ? renderArrivalsDeparturesView()
                                    : renderFloorView()}
                    </div>
                </div>

                {/* Reservation Profile Modal */}
                {showReservationModal && (
                    <ReservationProfile
                        show={showReservationModal}
                        onClose={() => {
                            setShowReservationModal(false);
                            setSelectedConfirmationNumber(null);
                            setSelectedAssignmentContext(null);
                        }}
                        confirmationNumber={selectedConfirmationNumber}
                        assignmentContext={selectedAssignmentContext}
                    />
                )}

                <BulkRoomAssignmentModal
                    show={showBulkRoomAssignmentModal}
                    onClose={() => {
                        setShowBulkRoomAssignmentModal(false);
                        getRoomStatus();
                    }}
                    onSuccess={() => {
                        setShowBulkRoomAssignmentModal(false);
                        getRoomStatus();
                    }}
                    propertiesId={selectedLoginData?.properties_id}
                    pmsPropertiesId={roomStatusesMeta?.pms_properties_id}
                    showDate={monitorShowDate}
                />
            </div>
        )
    );
};


export default MonitorIndex